import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import API from "Payments/PaymentPage/API";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { usePollForPaymentStatus } from "Payments/PaymentPage/Shared/usePollForPaymentStatus";
import qs from "qs";
import sanitizeHtml from "sanitize-html";

import { usePaymentTypeVerbiage } from "Utilities/Hooks/usePaymentTypeVerbiage";

import { usePaymentForm } from "../PaymentFormContext";

const useSubmitPayment = () => {
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [addOnError, setAddOnError] = useState(null);
  const location = useLocation();
  const {
    keyword: keywordURLParam,
    ttgSourceNumber,
    noteID: noteIDURLParam,
    userSignupItemIDs: userSignupItemIDsParam,
    pledgeId: pledgeIDParam,
  } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const { state, dispatch } = usePaymentPage();
  const { currentScreen } = state;
  const { noun } = usePaymentTypeVerbiage();

  const userSignupItemIDs = useMemo(() => {
    return userSignupItemIDsParam?.split(",");
  }, [userSignupItemIDsParam]);

  const pledgeID = useMemo(
    () => pledgeIDParam || window.data.paymentPageData?.campaign?.pledge?.ID,
    [pledgeIDParam]
  );

  const {
    state: { formStages, isSubmitting },
    dispatch: formDispatch,
  } = usePaymentForm();

  const setAmountError = useCallback(
    (message) => {
      formDispatch({
        type: "setAmountErrorMsg",
        payload: message,
      });
    },
    [formDispatch]
  );

  const setIsSubmitting = useCallback(
    (payload) => {
      formDispatch({
        type: "setIsSubmitting",
        payload,
      });
    },
    [formDispatch]
  );

  const runPresubmitValidations = useCallback(
    (addOnAmounts) => {
      if (!state.amount) {
        setAmountError("Please enter an amount");
        return false;
      } else if (Number(state.amount) < 2) {
        setAmountError("Please enter an amount greater than $2");
        return false;
      } else if (Number(state.amount) > 10000) {
        setAmountError("The maximum amount allowed is $10,000");
        return false;
      }

      let total = parseFloat(state.amount);
      for (const addOnGift of addOnAmounts) {
        if (addOnGift.isChecked) {
          // validate flat amount is not 0
          if (!addOnGift.amount || parseFloat(addOnGift.amount) <= 0) {
            setAddOnError("Please enter an Add On amount greater than $0");
            return false;
            // validate flat amount is less than $10,000
          } else if (parseFloat(addOnGift.amount) >= 10000) {
            setAddOnError("The maximum amount that can be added on is $10,000");
            return false;
          }
          total += parseFloat(addOnGift.amount);
        }
      }

      if (total > 10000) {
        setAmountError("The maximum amount allowed is $10,000");
        return false;
      }

      if (!state.selectedPaymentMethodID) {
        setPaymentMethodError("Please select a payment method");
        return false;
      }

      return true;
    },
    [setAmountError, state.amount, state.selectedPaymentMethodID]
  );

  usePollForPaymentStatus({
    forScreen: "paymentForm",
    setIsSubmitting,
    setSubmitError,
  });

  const getAddOnGiftsPostData = (addOnAmounts) => {
    if (!addOnAmounts?.length) return [];
    const addOnGifts = addOnAmounts.filter((a) => a.isChecked);
    return addOnGifts.map((a) => ({
      addOnGiftID: a.addOnID,
      amount: Math.round(parseFloat(a.amount || 0) * 100),
    }));
  };

  const submitPayment = useCallback(
    (addOnAmounts = []) => {
      const isValid = runPresubmitValidations(addOnAmounts);

      if (isValid) {
        setSubmitError(null);
        dispatch({ type: "resetPaymentSubmit" });
      } else return;

      const addOnGiftData = getAddOnGiftsPostData(addOnAmounts);

      const submitPaymentCall = API.submitPayment({
        tabID: state.tabID,
        fundID: state.fund.ID,
        amount: (Number(state.amount) * 100).toFixed(0), // in cents
        paymentMethodID: state.selectedPaymentMethodID,
        applyToExistingPledge: state.applyToExistingPledge,
        pledgeID: state.applyToExistingPledge && pledgeID ? pledgeID : null,
        paymentID: state.modifyPayment?.paymentID,
        keyword: keywordURLParam,
        noteID: noteIDURLParam,
        ttgSourceNumber,
        recurring: state.recurringPayment.recurring
          ? {
              startDate: state.recurringPayment.startDate,
              endDate: state.recurringPayment.endDate,
              interval: state.recurringPayment.frequency,
            }
          : null,
        memo: sanitizeHtml(state.memo),
        userSignupItemIDs: userSignupItemIDs,
        addOnGiftData,
      });

      setIsSubmitting(true);

      submitPaymentCall
        .post()
        .then((res) => {
          dispatch({
            type: "setSubmitPaymentResponse",
            payload: res,
          });
          // NOTE: the usePollForPaymentStatus hook takes over at this point and will update using setIsSubmitting.
        })
        .catch((err) => {
          setIsSubmitting(false);
          if (err.error === "already_processed") {
            dispatch({ type: "setModifyPaymentAlreadyCompleted" });
          } else if (err.error === "payment_already_processed_on_tab_id") {
            // this "user session" has already submitted a payment.
            // this is an unrecoverable error, so we need to force the user to refresh the page.
            dispatch({
              type: "forcePageReload",
              reason: "payment_already_processed_on_tab_id",
            });
          } else if (
            userSignupItemIDs?.length > 0 &&
            err.error === "invalid_amount"
          ) {
            // this is a signup payment, and the amount entered does not match the amount the user owes.
            setSubmitError(
              "The amount entered does not match the amount you currently owe for this signup."
            );
          } else if (err.error === "payments_killswitch_enabled") {
            setSubmitError(err.message);
          } else if (err.error === "fund_received_payment_too_recently") {
            setSubmitError(
              "You've already made a payment to this fund in the past " +
                err.lookbackWindowInMinutes +
                " minutes. Please wait to try again."
            );
          } else if (
            err.error === "unexpected_error_before_queueing" &&
            isSettingUpRecurringWithoutSavedPayment(state)
          ) {
            // TODO: use the network specific wording for "payment"
            setSubmitError(
              `Please use a saved payment method to make a recurring ${noun}.`
            );
          } else {
            console.log(state);
            setSubmitError(
              "There was a problem finalizing your payment. Please DO NOT try again. Contact help@flocknote.com about the status of your payment."
            );
          }
        });
    },
    [
      dispatch,
      keywordURLParam,
      noteIDURLParam,
      pledgeID,
      runPresubmitValidations,
      setIsSubmitting,
      state,
      ttgSourceNumber,
      userSignupItemIDs,
      noun,
    ]
  );

  useEffect(
    function clearErrorsWhenNotOnSubmitSection() {
      if (currentScreen === "paymentForm") return;

      dispatch({ type: "setSubmitPaymentResponse", payload: {} });
      setAmountError(null);
      setPaymentMethodError(null);
      setSubmitError(null);
    },
    [currentScreen, setAmountError, dispatch]
  );

  useEffect(
    function clearErrorsWhenNotOnSubmitSection() {
      if (currentScreen === "paymentForm") return;

      dispatch({ type: "setSubmitPaymentResponse", payload: {} });
      setAmountError(null);
      setPaymentMethodError(null);
      setSubmitError(null);
    },
    [currentScreen, setAmountError, dispatch]
  );

  return [
    { paymentMethodError, submitError, addOnError, formStages, isSubmitting },
    submitPayment,
  ];
};

const isSettingUpRecurringWithoutSavedPayment = (state) => {
  // eslint-disable-next-line no-debugger
  // debugger;
  const selectedPaymentMethodID = state.selectedPaymentMethodID;

  const selectedPaymentMethodIsSaved = state.paymentMethods.find(
    (item) => item.ID === selectedPaymentMethodID
  ).isAvailableAsSavedPaymentMethod;
  return !selectedPaymentMethodIsSaved;
};

export default useSubmitPayment;
